import type { ProficiencyLevelResponse } from './performanceProfileService';
import type {
    CareerAspiration,
    CareerAspirationHistoryEntry,
    CareerAspirationCreateRequest,
    CareerAspirationUpdateRequest,
    CareerStep,
    CareerStepRequest,
    EmployeeGrowth,
    NextLevelSkill,
} from '@/types/career-growth';
import type { LocalizedText } from '@/utils/localization';

import { api } from '@/api/api';

const growthUrl = (employeeId: string) => `/performance-profile/${employeeId}`;

/** One competency profile of the employee portal's evolution journey. */
export interface EvolutionProfile {
    name: LocalizedText;
    skills: NextLevelSkill[];
}

/**
 * A manager assessment the employee has received whose levels are held back until its meeting.
 * `evaluationDate` is the date the assessment itself carries, `null` when it carries none: nothing
 * records the instant it was submitted, so nothing here claims it.
 */
export interface WithheldEvaluation {
    evaluationDate: string | null;
}

/**
 * An employee's evolution journey (`GET performance-profile/me/evolution`): their assigned profile,
 * the profile of their target career step, and the scale to place their levels on. A profile is
 * `null` when none is set.
 */
export interface EmployeeEvolution {
    currentProfile: EvolutionProfile | null;
    desiredProfile: EvolutionProfile | null;
    proficiencyLevels: ProficiencyLevelResponse[];
    /**
     * The assessment the employee is waiting on, `null` when none is: either the company shows its
     * levels as soon as they are submitted, or no assessment of theirs is waiting for a meeting.
     */
    withheldEvaluation: WithheldEvaluation | null;
}

/**
 * The "Croissance" tab's endpoints, all scoped to one employee of the performance profile. The
 * tab reads its steps and aspiration history through the growth aggregate; a career meeting's plan,
 * which needs only the history, reads it alone. The journey notes live with the employee's other
 * notes, in `employeeNoteService`.
 */
export default {
    async getEmployeeGrowth(employeeId: string): Promise<EmployeeGrowth> {
        const response = await api.get(`${growthUrl(employeeId)}/growth`);
        return response.data;
    },

    /**
     * The journey the portal draws. Without an employee the caller reads their own; with one, that
     * employee's, as they themselves would read it -- the server answers 404 for anyone the
     * caller's performance scope does not reach.
     */
    async getMyEvolution(employeeId?: string): Promise<EmployeeEvolution> {
        const response = await api.get('/performance-profile/me/evolution', {
            params: { employeeId },
        });
        return response.data;
    },

    async createCareerStep(employeeId: string, request: CareerStepRequest): Promise<CareerStep> {
        const response = await api.post(`${growthUrl(employeeId)}/career-steps`, request);
        return response.data;
    },

    async updateCareerStep(
        employeeId: string,
        stepId: string,
        request: CareerStepRequest,
    ): Promise<CareerStep> {
        const response = await api.put(`${growthUrl(employeeId)}/career-steps/${stepId}`, request);
        return response.data;
    },

    async deleteCareerStep(employeeId: string, stepId: string): Promise<void> {
        await api.delete(`${growthUrl(employeeId)}/career-steps/${stepId}`);
    },

    async getCareerAspirationHistory(employeeId: string): Promise<CareerAspirationHistoryEntry[]> {
        const response = await api.get(`${growthUrl(employeeId)}/career-aspirations`);
        return response.data;
    },

    async getCareerAspiration(employeeId: string, aspirationId: string): Promise<CareerAspiration> {
        const response = await api.get(
            `${growthUrl(employeeId)}/career-aspirations/${aspirationId}`,
        );
        return response.data;
    },

    async createCareerAspiration(
        employeeId: string,
        request: CareerAspirationCreateRequest,
    ): Promise<CareerAspiration> {
        const response = await api.post(`${growthUrl(employeeId)}/career-aspirations`, request);
        return response.data;
    },

    async updateCareerAspiration(
        employeeId: string,
        aspirationId: string,
        request: CareerAspirationUpdateRequest,
    ): Promise<CareerAspiration> {
        const response = await api.put(
            `${growthUrl(employeeId)}/career-aspirations/${aspirationId}`,
            request,
        );
        return response.data;
    },
};
